import { useEffect, useRef, useState, type TransitionEvent } from 'react';
import styles from './CountdownFlipClock.module.css';

export type CountdownFlipClockProps = {
  /** Remaining time in milliseconds (clamped to ≥ 0 for display). */
  totalMs: number;
  className?: string;
};

function formatMmSs(totalMs: number): string {
  const sec = Math.max(0, Math.floor(totalMs / 1000));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function clampDigit(n: number): number {
  return Math.min(9, Math.max(0, Math.floor(n)));
}

function FlipDigit({ value }: { value: number }) {
  const v = clampDigit(value);
  const [front, setFront] = useState(v);
  const [back, setBack] = useState(v);
  const [flipping, setFlipping] = useState(false);
  const [instant, setInstant] = useState(false);
  const targetRef = useRef(v);
  const flipActiveRef = useRef(false);

  useEffect(() => {
    if (v === front) return;
    targetRef.current = v;
    setBack(v);
    flipActiveRef.current = true;
    setFlipping(true);
  }, [v, front]);

  const onTransitionEnd = (e: TransitionEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.propertyName !== 'transform') return;
    if (!flipActiveRef.current) return;
    flipActiveRef.current = false;
    setInstant(true);
    setFront(targetRef.current);
    setFlipping(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setInstant(false));
    });
  };

  return (
    <div className={styles.flipDigit} aria-hidden>
      <div
        className={[
          styles.flipInner,
          flipping ? styles.flipInnerFlipping : '',
          instant ? styles.flipInnerInstant : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onTransitionEnd={onTransitionEnd}
      >
        <div className={`${styles.face} ${styles.faceFront}`}>{front}</div>
        <div className={`${styles.face} ${styles.faceBack}`}>{back}</div>
      </div>
    </div>
  );
}

/**
 * MM:SS countdown with per-digit flip animation (flip-clock style).
 * Screen readers: polite live region with plain `mm:ss` text.
 */
export function CountdownFlipClock({ totalMs, className }: CountdownFlipClockProps) {
  const sec = Math.max(0, Math.floor(totalMs / 1000));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  const m10 = Math.floor(m / 10);
  const m1 = m % 10;
  const s10 = Math.floor(s / 10);
  const s1 = s % 10;
  const label = formatMmSs(totalMs);

  return (
    <div
      className={[styles.root, className].filter(Boolean).join(' ')}
      role="timer"
      aria-label={`Temps restant avant déconnexion : ${label}`}
    >
      <span className={styles.srOnly} aria-live="polite" aria-atomic="true">
        {label}
      </span>
      <div className={styles.row}>
        <FlipDigit value={m10} />
        <FlipDigit value={m1} />
        <span className={styles.sep} aria-hidden>
          :
        </span>
        <FlipDigit value={s10} />
        <FlipDigit value={s1} />
      </div>
    </div>
  );
}
